<template>
  <AnalyticsWidget
    :title="title"
    :subTitle="subTitle"
    :embedded="embedded"
    :gridWidth="gridWidth"
    :gridHeight="gridHeight">
    <div class="h-100">
      <ReportFilterPopover
        headerTitle="Moving Average Filter"
        :showApplyButton="true"
        :showResetButton="true"
        :appendToBody="true"
        alignment="left"
        placement="bottom-end"
        @apply="applyMovingAverageFilter"
        @reset="resetMovingAverageFilter">
        <template v-slot:popover>
          <ReportFilterGroup label="Date Filter">
            <ReportDateRangeSelect
              :startDate="editingMovingAverageFilter.startDate"
              :endDate="editingMovingAverageFilter.endDate"
              @updateStartDate="editingMovingAverageFilter.startDate = $event"
              @updateEndDate="editingMovingAverageFilter.endDate = $event"
            />
          </ReportFilterGroup>
          <ReportFilterGroup label="Stock Filter" v-if="stockFilterEnabled">
            <FormInputStockCode
              v-model="editingMovingAverageFilter.stockId"
              @updateStockInfo="editingMovingAverageFilter.stockInfo = $event"
              placeholder="Stock filter"
              :dropdownAutoWidth="true"
              :appendToBody="true"
              :stockType="InventoryStockTypes.glass" />
          </ReportFilterGroup>
        </template>
        <template v-slot:filterDisplay>
          <div>
            <i class="far fa-calendar mr-2"></i>{{appliedMovingAverageFilter.startDate | formatDate}} - {{appliedMovingAverageFilter.endDate | formatDate}}
          </div>
          <div v-if="hasStockFilter">
            <i class="far fa-inventory mr-2"></i>{{appliedMovingAverageFilter.stockInfo ? appliedMovingAverageFilter.stockInfo.name : 'None'}}
          </div>
        </template>
      </ReportFilterPopover>

      <ChartLine
        title="Moving Average"
        :series="stockMovingAverageData.series"
        :xAxis="stockMovingAverageData.xAxis"
        :yAxis="stockMovingAverageData.yAxis"
        :legend="stockMovingAverageData.legend"
        :options="stockMovingAverageData.options"
        :popupEnabled="true"
        :exportEnabled="true"
      />
    </div>
  </AnalyticsWidget>
</template>


<script>
import _ from 'lodash'
import moment from 'moment'

import AnalyticsWidget from '@/modules/intelligence/components/analyticsWidget'
import AnalyticsDashboardValueRow from '@/modules/intelligence/components/analyticsDashboardValueRow'
import AnalyticsDashboardValue from '@/modules/intelligence/components/analyticsDashboardValue'
import AnalyticsDashboardInfo from '@/modules/intelligence/components/analyticsDashboardInfo'

import ReportFilterPopover from '@/components/reporting/reportFilterPopover'
import ReportDateRangeSelect from '@/components/reporting/reportDateRangeSelect'
import ReportFilterGroup from '@/components/reporting/reportFilterGroup'
import FormInputStockCode from '@/modules/inventory/components/formInputStockCode'

import ChartLine from '@/components/charts/chartLine'
import { InventoryStockTypes } from '@/store/types'

export default {
  name: 'InventoryStockMovingAverageChartWidget',
  components: {
    AnalyticsWidget,
    AnalyticsDashboardValueRow,
    AnalyticsDashboardValue,
    AnalyticsDashboardInfo,
    ChartLine,
    ReportFilterPopover,
    ReportDateRangeSelect,
    ReportFilterGroup,
    FormInputStockCode
  },
  props: {
    title: String,
    subTitle: String,
    gridWidth: Number,
    gridHeight: Number,
    embedded: Boolean,
    stockFilterEnabled: {
      type: Boolean,
      default: true
    },
    stockId: Number
  },
  data() {
    return {
      stockMovingAverageData: {
        series: [],
        xAxis: {},
        yAxis: {},
        legend: {},
        options: {}
      },
      defaultMovingAverageFilter: {    
        startDate: moment().local().subtract(24, 'months').startOf('day').toDate().toISOString(),
        endDate: moment().local().endOf('day').toDate().toISOString(),
        stockId: this.stockId,
        stockInfo: {}
      },
      editingMovingAverageFilter: {},
      appliedMovingAverageFilter: {},
    }
  },
  computed: {
    hasStockFilter() {
      return !!this.defaultMovingAverageFilter.stockId && this.stockFilterEnabled
    }
  },
  created() {
    this.InventoryStockTypes = InventoryStockTypes
  },
  mounted() {
    this.editingMovingAverageFilter = _.clone(this.defaultMovingAverageFilter)
    this.appliedMovingAverageFilter = _.clone(this.defaultMovingAverageFilter)

    this.requestChartData()
  },
  watch: {
    stockId: function() {
      this.defaultMovingAverageFilter.stockId = this.stockId
      this.editingMovingAverageFilter.stockId = this.stockId
      this.appliedMovingAverageFilter.stockId = this.stockId

      this.requestChartData()
    }    
  },
  methods: {
    applyMovingAverageFilter() {
      this.appliedMovingAverageFilter = _.clone(this.editingMovingAverageFilter)

      this.requestChartData()
    },
    resetMovingAverageFilter() {
      this.editingMovingAverageFilter = _.clone(this.defaultMovingAverageFilter)
      this.appliedMovingAverageFilter = _.clone(this.defaultMovingAverageFilter)

      this.requestChartData()
    },
    requestChartData() {
      var stockMovingAverageFilter = {
        startDate: this.appliedMovingAverageFilter.startDate,
        endDate: this.appliedMovingAverageFilter.endDate,
        stockId: this.appliedMovingAverageFilter.stockId,
      }

      this.$apiService.analyticsService.getGlassSizeMovingAverageChartData(stockMovingAverageFilter)
      .then(response => {
        var glassSizeList = response.data

        this.stockMovingAverageData.legend = {
          data: glassSizeList.map(stockMovingAverageSeries => `${stockMovingAverageSeries.height}x${stockMovingAverageSeries.width}`)
        }

        this.stockMovingAverageData.xAxis = {
          type: 'time',
        }
        this.stockMovingAverageData.yAxis = {
          type: 'value',
          axisLabel: {
            formatter: (value) => {
              return this.formatCurrency(value, 1);
            }
          }
        }

        this.stockMovingAverageData.options.tooltip = {
          trigger: 'axis',
          axisPointer: {
            label: {
              formatter: function (params) {
                return moment(params.value).format('DD MMM YY');
              }
            }
          }
        }
        
        this.stockMovingAverageData.series = glassSizeList.map(glassSizeInfo => {
          return {
            type: 'line',
            name: `${glassSizeInfo.height}x${glassSizeInfo.width}`,
            data: glassSizeInfo.movingAverageList.map(movingAvg => {
              return [moment(movingAvg.logDate).format('YYYY-MM-DD'), movingAvg.unitValue]
            }),
            tooltip: {
              valueFormatter: (value) => {
                return this.formatCurrency(value);
              }
            }
          }
        })
      })
    },
  }
}
</script>